import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ImageIcon from '@mui/icons-material/Image';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const BannerSlideForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [title, setTitle] = useState("");
    const [heading, setHeading] = useState("");
    const [headingZh, setHeadingZh] = useState("");
    const [subheading, setSubheading] = useState("");
    const [subheadingZh, setSubheadingZh] = useState("");
    const [ctaText, setCtaText] = useState("Shop Now");
    const [ctaTextZh, setCtaTextZh] = useState("");
    const [ctaLink, setCtaLink] = useState("/products");
    const [order, setOrder] = useState(0);
    const [active, setActive] = useState(true);
    const [showOverlay, setShowOverlay] = useState(true);
    const [showButton, setShowButton] = useState(true);
    const [buttonPosition, setButtonPosition] = useState("center-center");
    const [showOnWebsite, setShowOnWebsite] = useState(true);
    const [showOnApp, setShowOnApp] = useState(true);

    const [image, setImage] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [imageZh, setImageZh] = useState("");
    const [imageZhPreview, setImageZhPreview] = useState("");
    const [mobileImage, setMobileImage] = useState("");
    const [mobileImagePreview, setMobileImagePreview] = useState("");
    const [mobileImageZh, setMobileImageZh] = useState("");
    const [mobileImageZhPreview, setMobileImageZhPreview] = useState("");

    // In edit mode, load the existing slide's data. There's no single-slide
    // GET endpoint on the backend, so we fetch the admin list and find this
    // one by id — fine since there will only ever be a handful of slides.
    useEffect(() => {
        if (!isEditMode) return;

        const fetchSlide = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/banner-slides');
                const slide = data.slides.find((s) => s._id === id);
                if (!slide) {
                    enqueueSnackbar("Banner slide not found", { variant: "error" });
                    navigate('/admin/banner-slides');
                    return;
                }
                setTitle(slide.title || "");
                setHeading(slide.heading || "");
                setHeadingZh(slide.headingZh || "");
                setSubheading(slide.subheading || "");
                setSubheadingZh(slide.subheadingZh || "");
                setCtaText(slide.ctaText || "Shop Now");
                setCtaTextZh(slide.ctaTextZh || "");
                setCtaLink(slide.ctaLink || "/products");
                setOrder(slide.order || 0);
                setActive(slide.active);
                setShowOverlay(slide.showOverlay !== false);
                setShowButton(slide.showButton !== false);
                setButtonPosition(slide.buttonPosition || "center-center");
                setShowOnWebsite(slide.showOnWebsite !== false);
                setShowOnApp(slide.showOnApp !== false);
                setImagePreview(slide.image?.url || "");
                setImageZhPreview(slide.imageZh?.url || "");
                setMobileImagePreview(slide.mobileImage?.url || "");
                setMobileImageZhPreview(slide.mobileImageZh?.url || "");
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load slide", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSlide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImagePreview(reader.result);
                setImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageZhChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImageZhPreview(reader.result);
                setImageZh(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobileImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setMobileImagePreview(reader.result);
                setMobileImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobileImageZhChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setMobileImageZhPreview(reader.result);
                setMobileImageZh(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            enqueueSnackbar("Please enter a title (for identifying this slide in the list)", { variant: "warning" });
            return;
        }
        if (!isEditMode && !image) {
            enqueueSnackbar("Please choose a slide image", { variant: "warning" });
            return;
        }

        const payload = { title, heading, headingZh, subheading, subheadingZh, ctaText, ctaTextZh, ctaLink, order, active, showOverlay, showButton, buttonPosition, showOnWebsite, showOnApp };
        if (image) payload.image = image;
        if (imageZh) payload.imageZh = imageZh;
        if (mobileImage) payload.mobileImage = mobileImage;
        if (mobileImageZh) payload.mobileImageZh = mobileImageZh;

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/banner-slide/${id}`, payload);
                enqueueSnackbar("Banner slide updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/banner-slide/new', payload);
                enqueueSnackbar("Banner slide created", { variant: "success" });
            }
            navigate('/admin/banner-slides');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save slide", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Banner Slide | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="bannerform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Banner Slide</h1>

                <TextField
                    label="Title"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    helperText="For identifying this slide in the admin list only — never shown on the website"
                />

                <TextField
                    label="Heading (optional)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    helperText="Shown as the large text on the slide — leave blank for an image-only slide"
                />

                <TextField
                    label="Heading (中文 — optional)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={headingZh}
                    onChange={(e) => setHeadingZh(e.target.value)}
                    helperText="Shown when a visitor switches to Chinese. Leave blank to keep showing the English heading."
                />

                <div>
                    <p className="text-sm text-gray-600 mb-1">Subheading</p>
                    <ReactQuill theme="snow" value={subheading} onChange={setSubheading} />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Subheading (中文 — optional)</p>
                    <ReactQuill theme="snow" value={subheadingZh} onChange={setSubheadingZh} />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Button Text"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={ctaText}
                        onChange={(e) => setCtaText(e.target.value)}
                    />
                    <TextField
                        label="Button Text (中文)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={ctaTextZh}
                        onChange={(e) => setCtaTextZh(e.target.value)}
                    />
                    <TextField
                        label="Button Link"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={ctaLink}
                        onChange={(e) => setCtaLink(e.target.value)}
                        helperText="e.g. /products or /products?category=grocery"
                    />
                    <TextField
                        label="Display Order"
                        type="number"
                        variant="outlined"
                        size="small"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        helperText="Lower numbers show first"
                    />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (visible on homepage)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOverlay} onChange={(e) => setShowOverlay(e.target.checked)} />}
                    label="Dark overlay (improves text readability over busy photos)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showButton} onChange={(e) => setShowButton(e.target.checked)} />}
                    label="Show button"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnWebsite} onChange={(e) => setShowOnWebsite(e.target.checked)} />}
                    label="Show on Website"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnApp} onChange={(e) => setShowOnApp(e.target.checked)} />}
                    label="Show on Customer App"
                />

                {showButton && (
                    <TextField
                        label="Button Position"
                        select
                        variant="outlined"
                        size="small"
                        value={buttonPosition}
                        onChange={(e) => setButtonPosition(e.target.value)}
                        sx={{ maxWidth: 260 }}
                    >
                        <MenuItem value="center-center">Center</MenuItem>
                        <MenuItem value="top-center">Top Center</MenuItem>
                        <MenuItem value="bottom-center">Bottom Center</MenuItem>
                        <MenuItem value="center-left">Center Left</MenuItem>
                        <MenuItem value="center-right">Center Right</MenuItem>
                        <MenuItem value="top-left">Top Left</MenuItem>
                        <MenuItem value="top-right">Top Right</MenuItem>
                        <MenuItem value="bottom-left">Bottom Left</MenuItem>
                        <MenuItem value="bottom-right">Bottom Right</MenuItem>
                    </TextField>
                )}

                <div>
                    <p className="text-sm text-gray-600 mb-1">Slide Image — used on the website</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imagePreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imagePreview} alt="Slide" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Slide Image (中文 — optional)</p>
                    <p className="text-xs text-gray-400 mb-2">
                        Shown instead of the image above when a visitor switches to Chinese — useful if your
                        banner image contains English text baked in. Leave blank to just reuse the image above.
                    </p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imageZhPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imageZhPreview} alt="Slide (Chinese)" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageZhChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Mobile App Image (optional)</p>
                    <p className="text-xs text-gray-400 mb-2">
                        The mobile app displays this slide in a different shape than the website — upload a
                        separate image here if the website image is cropping badly on the app. Leave blank to
                        just reuse the website image above.
                    </p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!mobileImagePreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={mobileImagePreview} alt="Mobile Slide" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleMobileImageChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Mobile App Image (中文 — optional)</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!mobileImageZhPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={mobileImageZhPreview} alt="Mobile Slide (Chinese)" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleMobileImageZhChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div className="flex justify-end">
                    <input form="bannerform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Slide"} />
                </div>

            </form>
        </>
    );
};

export default BannerSlideForm;